import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import suppliersService, { Supplier, SupplierInput } from '../../../services/supplierCrm/suppliersService';
import SupplierContactsDrawer from '../../supplier-crm/SupplierContactsDrawer';

type ViewMode = 'table' | 'kanban' | 'gallery';
type SupplierStatus = 'active' | 'onboarded' | 'on_hold' | 'blacklisted';

const STATUS_OPTIONS: Array<{ value: SupplierStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'blacklisted', label: 'Blacklisted' }
];

const SuppliersTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [items, setItems] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Supplier['status'] | ''>('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  
  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');
  const quickAddRef = useRef<HTMLInputElement | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeOrgId) return;

    setLoading(true);
    try {
      const { data } = await suppliersService.list({
        organization_id: activeOrgId,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        limit: 100,
        offset: 0
      });
      setItems(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, search, statusFilter]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const createSupplier = useCallback(async () => {
    if (!activeOrgId) {
      toast.error('Please select an organization first');
      return;
    }

    if (!name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    setLoading(true);
    try {
      const payload: SupplierInput = {
        organization_id: activeOrgId,
        name: name.trim(),
        category: category.trim() || undefined,
        email: contactEmail.trim() || undefined,
        phone: contactPhone.trim() || undefined,
        notes: notes.trim() || undefined,
        status: 'active'
      };
      await suppliersService.create(payload);
      setName('');
      setCategory('');
      setContactEmail('');
      setContactPhone('');
      setNotes('');
      toast.success('Supplier added');
      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create supplier');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, category, contactEmail, contactPhone, fetchData, name, notes]);

  const groupedByStatus = useMemo(() => {
    const grouped: Record<string, Supplier[]> = {};
    STATUS_OPTIONS.forEach((statusOpt) => {
      const status = statusOpt.value;
      grouped[status] = items.filter(item => item.status === status);
    });
    return grouped;
  }, [items]);

  return (
    <div className="space-y-6">
      {/* View Switcher & Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            📋 Table
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'kanban' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            📊 Kanban
          </button>
          <button
            onClick={() => setViewMode('gallery')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'gallery' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            🎴 Gallery
          </button>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Search suppliers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Supplier['status'] | '')}
          >
            <option value="">All Status</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Quick Add Form */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
        <h3 className="mb-4 text-sm font-semibold text-gray-700">➕ Quick Add Supplier</h3>
        <div className="grid gap-3 md:grid-cols-4">
          <input
            ref={quickAddRef}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Supplier name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
          <button
            onClick={() => void createSupplier()}
            disabled={loading}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'table' && (
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No suppliers found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Supplier</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Category</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Contact</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((supplier) => (
                    <tr key={supplier.id} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-primary">{supplier.name}</div>
                        {supplier.notes && (
                          <div className="text-xs text-gray-500 line-clamp-1">{supplier.notes}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{supplier.category || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                          {STATUS_OPTIONS.find(s => s.value === (supplier.status || 'active'))?.label || supplier.status || 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{supplier.email || '—'}</div>
                        {supplier.phone && <div className="text-xs text-gray-500">{supplier.phone}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedSupplier(supplier)}
                          className="rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === 'kanban' && (
        <div className="grid gap-4 md:grid-cols-4">
          {STATUS_OPTIONS.map((statusOpt) => (
            <div key={statusOpt.value} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <h4 className="mb-3 text-sm font-semibold text-gray-700">{statusOpt.label}</h4>
              <div className="space-y-2">
                {groupedByStatus[statusOpt.value]?.map((supplier: Supplier) => (
                  <div
                    key={supplier.id}
                    className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 transition hover:shadow-md"
                    onClick={() => setSelectedSupplier(supplier)}
                  >
                    <div className="font-medium text-gray-900">{supplier.name}</div>
                    {supplier.category && <div className="text-xs text-gray-500">{supplier.category}</div>}
                  </div>
                ))}
                {groupedByStatus[statusOpt.value]?.length === 0 && (
                  <div className="py-4 text-center text-xs text-gray-400">No suppliers</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'gallery' && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {items.map((supplier) => (
            <div
              key={supplier.id}
              className="cursor-pointer rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
              onClick={() => setSelectedSupplier(supplier)}
            >
              <div className="mb-2 text-2xl">🏢</div>
              <h4 className="mb-1 font-semibold text-gray-900">{supplier.name}</h4>
              <p className="mb-2 text-xs text-gray-500">{supplier.category || 'Uncategorized'}</p>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                {STATUS_OPTIONS.find(s => s.value === supplier.status)?.label || supplier.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Supplier Details Drawer */}
      <SupplierContactsDrawer
        isOpen={selectedSupplier != null}
        supplier={selectedSupplier}
        organizationId={selectedSupplier ? activeOrgId : null}
        onClose={() => setSelectedSupplier(null)}
        onContactsChanged={() => void fetchData()}
      />
    </div>
  );
};

export default SuppliersTab;
